// ════════════════════════════════════════════════════════════════════════════
// 🗄️  Tiny IndexedDB wrapper — Phase 1 Resilience
// ════════════════════════════════════════════════════════════════════════════
// Used by OfflineQueue to persist pending checkouts across browser reloads
// and network outages. Zero external deps. ~80 lines.
// ════════════════════════════════════════════════════════════════════════════

const DB_NAME = 'kangavena_pos';
const DB_VERSION = 1;
const STORE_OFFLINE_TX = 'offline_transactions';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_OFFLINE_TX)) {
        const os = db.createObjectStore(STORE_OFFLINE_TX, { keyPath: 'txRef' });
        os.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

export interface OfflineTx {
  txRef: string;
  payload: any;       // full /api/pos-checkout body
  createdAt: number;  // Date.now()
  attempts: number;
  lastError?: string;
}

export const idb = {
  async put(tx: OfflineTx): Promise<void> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const t = db.transaction(STORE_OFFLINE_TX, 'readwrite');
      t.objectStore(STORE_OFFLINE_TX).put(tx);
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
    });
  },

  async getAll(): Promise<OfflineTx[]> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const t = db.transaction(STORE_OFFLINE_TX, 'readonly');
      const req = t.objectStore(STORE_OFFLINE_TX).getAll();
      req.onsuccess = () => {
        const all = (req.result || []) as OfflineTx[];
        all.sort((a, b) => a.createdAt - b.createdAt);
        resolve(all);
      };
      req.onerror = () => reject(req.error);
    });
  },

  async delete(txRef: string): Promise<void> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const t = db.transaction(STORE_OFFLINE_TX, 'readwrite');
      t.objectStore(STORE_OFFLINE_TX).delete(txRef);
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
    });
  },

  async count(): Promise<number> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const t = db.transaction(STORE_OFFLINE_TX, 'readonly');
      const req = t.objectStore(STORE_OFFLINE_TX).count();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
};
